import { z } from "zod";

export const emailStatuses = [
  "queued",
  "sending",
  "sent",
  "delivered",
  "delivery_delayed",
  "bounced",
  "complained",
  "failed",
] as const;
export type EmailStatus = (typeof emailStatuses)[number];

export const emailStatusLabels: Record<EmailStatus, string> = {
  queued: "Queued",
  sending: "Sending",
  sent: "Sent",
  delivered: "Delivered",
  delivery_delayed: "Delayed",
  bounced: "Bounced",
  complained: "Marked as spam",
  failed: "Failed",
};

/** Statuses that may still change (the UI keeps polling while any are shown). */
export const pendingEmailStatuses: readonly EmailStatus[] = [
  "queued",
  "sending",
  "sent",
  "delivery_delayed",
];

export const MAX_BCC = 5;
export const MAX_EMAIL_HTML = 50_000;

/**
 * Composing an email to a lead. There's deliberately no `to`: it is always
 * the lead's own address. BCC is limited to Purlink teammates.
 */
export const composeEmailSchema = z
  .object({
    subject: z
      .string()
      .trim()
      .min(1, "Add a subject.")
      .max(200, "Keep the subject under 200 characters."),
    bodyHtml: z
      .string()
      .min(1, "Write a message.")
      .max(MAX_EMAIL_HTML, "That message is too long. Please shorten it."),
    bccUserIds: z
      .array(z.string().min(1).max(64))
      .max(MAX_BCC, `BCC up to ${MAX_BCC} people.`)
      .default([]),
  })
  .strict();
export type ComposeEmailInput = z.input<typeof composeEmailSchema>;

const isoDate = z.iso.date("Use a valid date.");

/** Emails page filters (also used as URL search params). */
export const emailListQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  status: z.enum(emailStatuses).optional(),
  senderId: z.string().max(64).optional(),
  sentFrom: isoDate.optional(),
  sentTo: isoDate.optional(),
  cursor: z.string().max(500).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type EmailListQuery = z.input<typeof emailListQuerySchema>;
